import * as amplitude from "@amplitude/analytics-browser";
import axios from "axios";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Spinner from "../components/Loading/Spinner";
import Store from "../stateStore";
import { getBackendUrl } from "../utils";
import ConfirmationScreen from "./ConfirmationScreen";
import CreditCardInputForm from "./CreditCardInputForm";
import ErrorScreen from "./ErrorScreen";
import { parseQueryParams, postMessageToOpener } from "./utils";

export default function PaymentMethod() {
  const history = useHistory();

  const { id: cardActivationId } = useParams<{ id: string }>();
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(true);
  const [vendorName, setVendorName] = useState<string | null>(null);
  const [vendorLogo, setVendorLogo] = useState<string | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [cardSubmissionError, setCardSubmissionError] = useState<string | null>(
    null,
  );

  const stateStore = Store.useStore();
  const orgId = stateStore.get("orgId");
  const { origin, nonce } = parseQueryParams(history.location.search);

  const setCardActivationDetails = (data: any) => {
    setVendorName(data.vendor_name);
    setVendorLogo(data.vendor_logo);
    setOrganizationName(data.org_name);
  };

  useEffect(() => {
    loadCardActivationFromId(cardActivationId);
  }, [cardActivationId]);

  const loadCardActivationFromId = async (
    id: string,
    options?: { setLoading?: boolean },
  ) => {
    if (options?.setLoading !== false) {
      setInitialLoading(true);
    }
    setError(null);

    try {
      console.log(`[v0] Loading card activation from ID: ${id}`);
      const response = await axios.get(
        `${getBackendUrl()}/supplier_enablement/check_payment_method/${id}`,
      );
      const data = response.data;
      console.log("[v0] Card activation data loaded:", data);

      setCardActivationDetails(data);
    } catch (err: any) {
      console.log("[v0] Error loading card activation:", err);

      // Check if the error response contains an error_message
      const errorMessage =
        err.response?.data?.error_message ??
        err.message ??
        "Failed to load card activation";
      setError(errorMessage);
    } finally {
      if (options?.setLoading !== false) {
        setInitialLoading(false);
      }
    }
  };

  // On card submission, redirect to complete page
  const handleCardSubmissionSuccess = async () => {
    try {
      console.log(
        `[v0] Card submission successful for card activation: ${cardActivationId}`,
      );

      amplitude.track("Payment Method Card Submission Success", {
        orgId,
        cardActivationId,
      });

      // Post message to opener if in popup
      try {
        postMessageToOpener(cardActivationId, nonce, origin);
      } catch (e) {
        console.error("[v0] Error posting message to opener:", e);
      }

      // Redirect to complete page
      history.push("/complete");
    } catch (err) {
      console.error("[v0] Error after card submission:", err);
      amplitude.track("Payment Method Card Submission Error", {
        orgId,
        cardActivationId,
        error: err instanceof Error ? err.message : "Unknown error",
      });
      setCardSubmissionError(
        "An unexpected error occurred. Please try refreshing the page. If the issue persists, contact support.",
      );
    }
  };

  // Show loading spinner first
  if (initialLoading) {
    return <Spinner />;
  }

  // Show error screen if there's an error
  if (error) {
    return (
      <ErrorScreen
        title="Error Loading Card Activation"
        message={error}
        onRetry={() => window.location.reload()}
        isRetrying={initialLoading}
      />
    );
  }

  // Show confirmation screen first
  if (showConfirmation) {
    return (
      <ConfirmationScreen
        vendorName={vendorName}
        vendorLogo={vendorLogo}
        organizationName={organizationName}
        title={`Update Payment Method for ${vendorName}`}
        description={
          <>
            <span className="font-semibold">{organizationName}</span> will
            securely update your default payment method for <b>{vendorName}</b>{" "}
            on your behalf.
          </>
        }
        buttonText="Continue"
        onContinue={() => {
          amplitude.track("Payment Method Confirmation Continue Clicked", {
            orgId,
            cardActivationId,
          });
          setShowConfirmation(false);
        }}
      />
    );
  }

  if (!cardActivationId) {
    return (
      <ErrorScreen
        icon="⚠️"
        title="Card Activation Not Available"
        message="The card activation information is missing. Please try reloading the page or contact support if the issue persists."
        buttonText="Reload"
        onRetry={() => window.location.reload()}
        isRetrying={initialLoading}
      />
    );
  }

  return (
    <CreditCardInputForm
      cardActivationId={cardActivationId}
      sessionId={null}
      onSuccess={handleCardSubmissionSuccess}
      externalError={cardSubmissionError}
      onClearExternalError={() => setCardSubmissionError(null)}
    />
  );

  return null;
}
